import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useNavigation, useFocusEffect } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { TimeBasedService } from "../../../services/TimeBasedService";
import { getMinutesValidationError, parseValidMinutes } from "../../../shared/utils/minutesValidation";

type EnumItem = {
  enumerationId: string;
  value: string;
  parameter?: string | number | null;
};

type TotalWorshipsDto = {
  totalMinutes: number;
  totalMarks: number;
  minutesOfWorships: number;
  marksOfWorships: number;
  existingEnumValue?: number | null;
  isEditable: boolean;
};

/* ---------- TIME HELPERS ---------- */

const timeOnlyToMinutes = (value: any) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parts = value.split(":");
    const h = parseInt(parts[0] || "0", 10);
    const m = parseInt(parts[1] || "0", 10);
    return h * 60 + m;
  }

  return 0;
};

const minutesToTimeOnly = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;

  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");

  return `${hh}:${mm}:00`;
};

export function DeityWorshipScreen() {
  const navigation = useNavigation<any>();
  const { user } = useAuth();

  const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const [date, setDate] = useState(todayStr);

  const [loading, setLoading] = useState(false);

  // WorshipType = Deity
  const [worshipTypeId, setWorshipTypeId] = useState<string | null>(null);
  const [parameter, setParameter] = useState<number>(0.2); // marks per minute default

  // entry data
  const [minutes, setMinutes] = useState<string>("");
  const [marks, setMarks] = useState<number>(0);
  const minutesValidationError = useMemo(() => getMinutesValidationError(minutes), [minutes]);

  // summary data
  const [totalMinutes, setTotalMinutes] = useState<number>(0);
  const [totalMarks, setTotalMarks] = useState<number>(0);
  const [isEditable, setIsEditable] = useState(true);

  // for update (if your API supports upsert without id, keep null)
  const [worshipId, setWorshipId] = useState<string | null>(null);

  const worshipTypeLabel = "Deity Worship";

  const minutesPerMarkText = useMemo(() => {
    // parameter = marks per minute, so minutes per 1 mark = 1/parameter
    if (!parameter || parameter <= 0) return "5 Minutes = 1 Mark";
    const m = Math.round(1 / parameter);
    return `${m} Minutes = 1 Mark`;
  }, [parameter]);

  const calcMarks = useCallback(
    (mins: number) => Number((mins * (parameter || 0)).toFixed(2)),
    [parameter]
  );

  useEffect(() => {
    const mins = parseValidMinutes(minutes) ?? 0;
    setMarks(calcMarks(mins));
  }, [minutes, calcMarks]);

  const openDatePicker = () => {
    DateTimePickerAndroid.open({
      value: new Date(date),
      mode: "date",
      maximumDate: new Date(),
      onChange: (event, selectedDate) => {
        if (event.type === "set" && selectedDate) {
          setDate(format(selectedDate, "yyyy-MM-dd"));
        }
      },
    });
  };

  const loadEnumerationAndSetDeity = useCallback(async () => {
    try {
      setLoading(true);

      const res = await TimeBasedService.getEnumeration("WorshipType");
      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to load worship types");
        return;
      }

      const list = (res?.data || []) as EnumItem[];

      // ✅ As per your requirement: WorshipType value = "Deity"
      const deity = list.find(
        (x) => (x.value || "").trim().toLowerCase() === "deity"
      );

      if (!deity?.enumerationId) {
        Flash.error("Deity worship type not found in Enumeration");
        return;
      }

      setWorshipTypeId(deity.enumerationId);

      const pRaw = deity.parameter;
      const p =
        typeof pRaw === "number"
          ? pRaw
          : typeof pRaw === "string"
            ? parseFloat(pRaw)
            : 0;

      setParameter(Number.isFinite(p) && p > 0 ? p : 0.2);
    } catch (e: any) {
      Flash.error(e?.message || "Enumeration load error");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAgainstDate = useCallback(
    async (dateStr: string) => {
      if (!user?.userId) return;
      if (!worshipTypeId) return;

      try {
        setLoading(true);

        const res = await TimeBasedService.getWorshipAgainstDate({
          date: dateStr,
          worshipType: worshipTypeId,
        });

        if (res?.status !== 200) {
          Flash.error(res?.message || "Failed to load");
          return;
        }

        const data = (res?.data || null) as TotalWorshipsDto | null;

        setTotalMinutes(data?.totalMinutes ?? 0);
        setTotalMarks(Number(data?.totalMarks ?? 0));
        setIsEditable(data?.isEditable ?? true)

        const mins = timeOnlyToMinutes(data?.minutesOfWorships);
        setMinutes(String(mins));
        setMarks(Number(data?.marksOfWorships ?? 0));

        // If your API later returns worshipId in this response, set it here.
        // setWorshipId(data?.worshipId ?? null);
        setWorshipId(null);
      } catch (e: any) {
        Flash.error(e?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [user?.userId, worshipTypeId]
  );

  useEffect(() => {
    loadEnumerationAndSetDeity();
  }, [loadEnumerationAndSetDeity]);

  useEffect(() => {
    loadAgainstDate(date);
  }, [date, loadAgainstDate]);

  // ✅ When coming back from History -> refresh same date
  useFocusEffect(
    useCallback(() => {
      loadAgainstDate(date);
    }, [date, loadAgainstDate])
  );

  const onSave = async () => {
    if (!user?.userId) {
      Flash.error("User not found");
      return;
    }
    if (!worshipTypeId) {
      Flash.error("Worship type not ready");
      return;
    }

    if (minutesValidationError) {
      Flash.error(minutesValidationError);
      return;
    }

    const mins = parseValidMinutes(minutes) ?? 0;
    const m = calcMarks(mins);

    try {
      setLoading(true);

      const payload = {
        worshipId: worshipId ?? null,
        userId: user.userId,
        date,
        worshipType: worshipTypeId,
        minutes: minutesToTimeOnly(mins),
        marks: m,
      };

      const res = await TimeBasedService.create(payload);

      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to save");
        return;
      }

      Flash.success("Success!", "Deity Worship Saved Successfully!");
      navigation.navigate("DeityWorshipHistory");
      // await loadAgainstDate(date);
    } catch (e: any) {
      Flash.error(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const calculatedMarks = useMemo(() => (marks), [marks]);

  return (
    <DivineBackground overlayOpacity={0.92}>
      <View style={{ flex: 1 }}>
        <Header title={worshipTypeLabel} showBack />

        <ScrollView
          contentContainerStyle={styles.container}
          showsVerticalScrollIndicator={false}
        >
          {/* Summary Card (same layout as Reading) */}
          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>This Month&apos;s Offering</Text>

            <View style={styles.summarySplit}>
              <View style={{ flex: 1 }}>
                <Text style={styles.summaryMini}>TOTAL MINUTES</Text>
                <Text style={styles.summaryValue}>{totalMinutes}</Text>
              </View>

              <View style={{ flex: 1, alignItems: "center" }}>
                <View style={styles.divider} />
              </View>

              <View style={{ flex: 1, alignItems: "flex-start" }}>
                <Text style={styles.summaryMini}>TOTAL MARKS</Text>
                <Text style={styles.summaryValue}>
                  {Number(totalMarks).toFixed(1)}
                </Text>
              </View>
            </View>
          </View>

          {/* Form Card */}
          <View style={styles.card}>
            <View style={styles.topRow}>
              <Pressable onPress={openDatePicker} style={styles.dateChip}>
                <Text style={styles.dateChipIcon}>📅</Text>
                <Text style={styles.dateChipText}>
                  {format(new Date(date), "dd-MM-yyyy")}
                </Text>
              </Pressable>

              <Pressable
                onPress={() => navigation.navigate("DeityWorshipHistory")}
                style={styles.historyBtn}
              >
                <Text style={styles.historyIcon}>🕘</Text>
                <Text style={styles.historyText}>History</Text>
              </Pressable>
            </View>

            <View style={{ marginTop: 14, gap: 10 }}>
              <Input
                label="Number of Minutes"
                keyboardType="number-pad"
                value={minutes}
                placeholder="e.g. 30"
                onChangeText={(t) => setMinutes(t.replace(/[^\d]/g, ""))}
                containerStyle={{ gap: 8 }}
              />
              {minutesValidationError ? <Text style={styles.validationText}>{minutesValidationError}</Text> : null}

              {minutesPerMarkText ? (
                <Text style={styles.helperText}>{minutesPerMarkText}</Text>
              ) : null}

              <View style={styles.calcRow}>
                <Text style={styles.calcLabel}>Calculated Marks:</Text>
                <Text style={styles.calcValue}>{calculatedMarks}</Text>
              </View>

              {isEditable && (
                <View style={styles.btnRow}>
                  <Pressable
                    onPress={() => navigation.goBack()}
                    disabled={loading}
                    style={({ pressed }) => [
                      styles.cancelBtn,
                      pressed ? { opacity: 0.95 } : null,
                      loading ? { opacity: 0.6 } : null,
                    ]}
                  >
                    <Text style={styles.cancelText}>Cancel</Text>
                  </Pressable>

                  <Pressable disabled={loading || !!minutesValidationError} onPress={onSave} style={{ flex: 1 }}>
                    <LinearGradient
                      // ✅ Deity gradient like screenshot (red → pink)
                      colors={["#EF4444", "#BE123C"]}
                      start={{ x: 0, y: 0.5 }}
                      end={{ x: 1, y: 0.5 }}
                      style={[styles.saveBtn, loading ? { opacity: 0.75 } : null]}
                    >
                      <Text style={styles.saveText}>
                        {loading ? "Saving..." : "Save"}
                      </Text>
                    </LinearGradient>
                  </Pressable>
                </View>
              )}

              {loading ? (
                <View style={{ marginTop: 10, alignItems: "center" }}>
                  <ActivityIndicator />
                </View>
              ) : null}
            </View>
          </View>
        </ScrollView>
      </View>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  container: { padding: 14, paddingBottom: 30, gap: 12 },

  // ✅ Offering card (same layout as Reading, Deity red theme)
  summaryCard: {
    borderRadius: 14,
    padding: 14,
    backgroundColor: "rgba(255,255,255,0.82)",
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.15)",
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
    elevation: 3,
  },
  summaryTitle: { fontSize: 13, fontWeight: "900", color: "rgba(17,24,39,0.85)" },

  summarySplit: { flexDirection: "row", alignItems: "center", marginTop: 10 },
  summaryMini: {
    fontSize: 10,
    fontWeight: "900",
    color: "rgba(0,0,0,0.35)",
    letterSpacing: 0.7,
  },
  summaryValue: { marginTop: 4, fontSize: 20, fontWeight: "900", color: "#B91C1C" },

  divider: { width: 1, height: 40, backgroundColor: "rgba(0,0,0,0.06)" },

  card: {
    borderRadius: 16,
    padding: 14,
    backgroundColor: "rgba(255,255,255,0.90)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    shadowColor: "#000",
    shadowOpacity: 0.10,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 10 },
    elevation: 4,
  },

  topRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },

  dateChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(239,68,68,0.08)",
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.18)",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
  },
  dateChipIcon: { fontSize: 14, opacity: 0.9 },
  dateChipText: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.85)" },

  historyBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    height: 34,
    borderRadius: 10,
    backgroundColor: "rgba(239,68,68,0.06)",
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.12)",
  },
  historyIcon: { fontSize: 14 },
  historyText: { fontSize: 12, fontWeight: "900", color: "#B91C1C" },
  validationText: { marginTop: -4, fontSize: 11, fontWeight: "800", color: "#DC2626" },

  helperText: { marginTop: -6, fontSize: 11, fontWeight: "800", color: "rgba(17,24,39,0.55)" },

  calcRow: {
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "rgba(239,68,68,0.06)",
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.12)",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  calcLabel: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.60)" },
  calcValue: { fontSize: 18, fontWeight: "900", color: "#B91C1C" },

  btnRow: { flexDirection: "row", gap: 10, marginTop: 6 },

  cancelBtn: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.10)",
    backgroundColor: "rgba(255,255,255,0.95)",
    alignItems: "center",
    justifyContent: "center",
  },
  cancelText: { fontSize: 14, fontWeight: "900", color: "rgba(17,24,39,0.55)" },

  saveBtn: {
    height: 46,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 4,
  },
  saveText: { fontSize: 14, fontWeight: "900", color: "#fff" },
});
